import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { useAuth } from '../../../hooks/useAuth'
import { useLinkToken } from '../../../hooks/useLinkToken'
import { confirmEmailChange } from '../../../api/users'
import { isRateLimited } from '../../../helpers/rate_limit'
import { theme } from '../../../theme/theme'
import { Button, ButtonVariant } from '../../../components/common/Button'
import { Icon, IconChoices } from '../../../components/common/Icon'
import { SpinnerWrapper } from '../../../components/common/SpinnerWrapper'
import { ErrorBoundary } from '../../../components/common/ErrorBoundary'
import {
  Illustration,
  IllustrationChoices,
} from '../../../components/common/Illustration'

interface LayoutProps {
  children: React.ReactNode
}

const ConfirmEmailChangeLayout = ({
  children,
}: LayoutProps): React.ReactNode => {
  return (
    <ErrorBoundary>
      <div className="flex h-full w-full flex-row">
        <div className="mx-6 h-full flex-1">
          <Icon
            className="mt-8"
            icon={IconChoices.BOAPPA}
            width={123}
            height={45}
          />
          <div className="mt-8 flex flex-col md:ml-32 md:mt-52">{children}</div>
        </div>
        <div
          className="flex hidden h-full w-full flex-1 items-center
         justify-center bg-brandLightGreen lg:flex"
        >
          <Illustration
            illustrationChoice={IllustrationChoices.SUN}
            width={400}
          />
        </div>
      </div>
    </ErrorBoundary>
  )
}

/**
 * Where the mail sent to a new email address lands. Opening the link is what
 * actually moves the account onto that address -- asking for the change only
 * parked it as pending -- so this page submits on arrival rather than asking
 * for anything: whoever opened it has already proved the one thing that was
 * missing, that somebody reads this inbox.
 *
 * No session is involved. The link is routinely opened in a different browser
 * from the one that asked for the change, and the account was proved with a
 * password back then, so there is nothing to sign in for here.
 */
export const ConfirmEmailChangeView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  useDocumentTitle(reverseDocumentTitle('confirm-email-change'))
  const token = useLinkToken()
  const navigate = useNavigate()
  const { userId } = useAuth()

  const hasToken = token != null && token.length > 0

  // The outcome is kept here rather than read off the mutation, and that is not
  // a preference. StrictMode tears the subscription down and sets it up again
  // after the first effect pass, and a MutationObserver does not re-attach to a
  // mutation already in flight -- so its status freezes on "pending" and the
  // page spins forever, even though the request had come back. The mutation's own
  // callbacks fire regardless of any of that, so they are what we listen to.
  const [outcome, setOutcome] = useState<
    'checking' | 'done' | 'limited' | 'failed'
  >('checking')

  const { mutate: mutateConfirm } = useMutation({
    mutationFn: async (code: string) => {
      const response = await confirmEmailChange(code)
      return response.data.data
    },
    onSuccess: () => setOutcome('done'),
    // A limit turns the request away before the code is looked at, so the link
    // still works. Calling it dead would send them off to ask for a new change,
    // which retires this link -- they only have to wait.
    onError: (error) => setOutcome(isRateLimited(error) ? 'limited' : 'failed'),
  })

  const retry = (): void => {
    setOutcome('checking')
    mutateConfirm(token as string)
  }

  // Submitted once, and only once. useLinkToken moves the token out of the
  // address bar on arrival, and StrictMode runs this twice in development --
  // the ref survives both, so the link is spent once. It has to be: a second
  // attempt would fail by design, because the link is single use.
  const submitted = useRef(false)
  useEffect(() => {
    if (!hasToken || submitted.current) {
      return
    }

    submitted.current = true
    mutateConfirm(token as string)
  }, [hasToken, token, mutateConfirm])

  const goOn = (): void => {
    navigate(userId ? reverseUrl('feed') : reverseUrl('login'), {
      replace: true,
    })
  }

  // No token at all: somebody typed the path, or the link lost its query on the
  // way through a mail client. Nothing to wait for, so say so at once.
  const failed = !hasToken || outcome === 'failed'
  const limited = !failed && outcome === 'limited'

  if (!failed && outcome === 'checking') {
    return (
      <ConfirmEmailChangeLayout>
        <div className="flex max-w-[500px] flex-col">
          <p style={theme.textVariants.h2} className="mt-3">
            {translate('confirmEmailChangeView.pending.title')}
          </p>
          <SpinnerWrapper />
        </div>
      </ConfirmEmailChangeLayout>
    )
  }

  const getSection = (): 'failure' | 'limited' | 'success' => {
    if (failed) {
      return 'failure'
    }
    return limited ? 'limited' : 'success'
  }
  const section = getSection()

  return (
    <ConfirmEmailChangeLayout>
      <div className="flex max-w-[500px] flex-col">
        <p style={theme.textVariants.h2} className="mt-3">
          {translate(`confirmEmailChangeView.${section}.title`)}
        </p>
        <p
          style={theme.textVariants.base}
          className="mt-2 [overflow-wrap:anywhere]"
        >
          {translate(`confirmEmailChangeView.${section}.subtitle`)}
        </p>
        {limited && (
          <Button
            wrapperClassName="mt-6 w-full md:w-80"
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            onClick={retry}
            label={translate('common.actions.tryAgain')}
          />
        )}
        <Button
          wrapperClassName={`${limited ? 'mt-3' : 'mt-6'} w-full md:w-80`}
          className="w-full"
          variant={limited ? ButtonVariant.SECONDARY : ButtonVariant.PRIMARY}
          onClick={goOn}
          label={translate(
            userId
              ? 'confirmEmailChangeView.continueButtonTitle'
              : 'confirmEmailChangeView.loginButtonTitle'
          )}
        />
      </div>
    </ConfirmEmailChangeLayout>
  )
}
